import { CalendarFilled, FireFilled, RightOutlined } from '@ant-design/icons'
import {
  Checkbox,
  ConfigProvider,
  DatePicker,
  Input,
  message,
  Tag,
  TimePicker,
  Typography,
} from 'antd'
import locale from 'antd/es/date-picker/locale/ko_KR'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import React, { useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import {
  createEpic,
  createStory,
  createTask,
  // 체크리스트 개별 조작 API
  createTaskCheckList,
  deleteEpic,
  deleteStory,
  deleteTask,
  deleteTaskCheckList,
  getGroupByTeam,
  getParentWorkItems,
  getProgressState,
  getTaskOwnables,
  getWorkDetail,
  switchTaskCheckList,
  updateEpic,
  updateStory,
  updateTask,
  updateTaskCheckListTitle,
} from '../../api/aops.api'
import AopsCommonModalLayout from '../../components/apos/modal/AopsCommonModalLayout'
import EmojiDropDown from '../../components/apos/EmojiDropDown'
import ModalStatusButtonGroup from '../../components/apos/modal/ModalStatusButtonGroup'
import { userState } from '../../store/user'
import './Board.scss'

const { Text } = Typography

const CATEGORY_BACKEND_MAP = {
  개선: 'IMPROVEMENT',
  신규: 'NEW_FEATURE',
  '정책/기준': 'POLICY_STANDARD',
  '사고 대응': 'INCIDENT_RESPONSE',
  기타: 'ETC',
}

const CATEGORY_FRONTEND_MAP = {
  IMPROVEMENT: '개선',
  NEW_FEATURE: '신규',
  POLICY_STANDARD: '정책/기준',
  INCIDENT_RESPONSE: '사고 대응',
  ETC: '기타',
}

/** 맵핑 헬퍼 함수 */
const mapBackendStateToFrontend = (state) => {
  const map = {
    TODO: 'To do',
    IN_PROGRESS: 'In progress',
    IN_REVIEW: 'In review',
    DONE: 'Done',
    ON_HOLD: 'On hold',
  }
  return map[state] || state
}

const mapPriorityToFrontendColor = (priority) => {
  switch (priority) {
    case 'FIRST':
      return '#F25A67'
    case 'SECOND':
      return '#FFCA00'
    case 'THIRD':
      return '#1FC297'
    default:
      return ''
  }
}

const mapProgressStateAndPriorityToFrontendColor = (
  progressState,
  priority,
) => {
  const mapInProgressPriorityToFrontendColor = (p) => {
    switch (p) {
      case 'FIRST':
        return '#FF7853'
      case 'SECOND':
        return '#FFCA00'
      case 'THIRD':
        return '#1FC297'
      default:
        return ''
    }
  }
  const mapTodoPriorityToFrontendColor = (p) => {
    switch (p) {
      case 'FIRST':
        return '#FFCCB5'
      case 'SECOND':
        return '#FFF0B5'
      case 'THIRD':
        return '#D9FCED'
      default:
        return ''
    }
  }
  switch (progressState) {
    case 'TODO':
      return mapTodoPriorityToFrontendColor(priority)
    case 'IN_PROGRESS':
      return mapInProgressPriorityToFrontendColor(priority)
    case 'IN_REVIEW':
      return mapInProgressPriorityToFrontendColor(priority)
    case 'DONE':
      return mapTodoPriorityToFrontendColor(priority)
    case 'ON_HOLD':
      return mapTodoPriorityToFrontendColor(priority)
    default:
      return ''
  }
}

const mapFrontendStateToBackend = (state) => {
  const map = {
    'To do': 'TODO',
    'In progress': 'IN_PROGRESS',
    'In review': 'IN_REVIEW',
    Done: 'DONE',
    'On hold': 'ON_HOLD',
  }
  return map[state] || ''
}

const mapPriorityToFrontend = (p) => {
  const map = { FIRST: '1순위', SECOND: '2순위', THIRD: '3순위' }
  return map[p] || ''
}

const mapPriorityToBackend = (p) => {
  const map = { '1순위': 'FIRST', '2순위': 'SECOND', '3순위': 'THIRD' }
  return map[p] || ''
}

const HELPER_DESC = {
  EPIC: (
    <Text>
      사업부가 이번 프로젝트를 통해 가져가야 하는 큰 <Text strong>방향성</Text>
      과 성과 단위
    </Text>
  ),
  STORY: (
    <Text>
      Objective를 달성하기 위해 해결해야 하는 <Text strong>목적 </Text>중심의
      문제 단위
    </Text>
  ),
  TASK: (
    <Text>
      Initiative를 완료하기 위해 담당자가 작업해야 하는{' '}
      <Text strong>실행 </Text>
      단위 작업
    </Text>
  ),
}

/** 섹션 레이아웃 */
const FormSection = ({
  label,
  children,
  helperTitle,
  helperDescText,
  vertical = false,
}) => (
  <div className={`form-section ${vertical ? 'vertical' : ''}`}>
    <label className="form-label">{label}</label>
    <div style={{ flex: 1, minWidth: 0 }}>
      {children}
      {helperTitle && (
        <div className="helper-box">
          <Text className="helper-title">{helperTitle}</Text>
          {helperDescText}
        </div>
      )}
    </div>
  </div>
)

const DateRangePicker = ({
  type,
  startDate,
  endDate,
  onChange,
  disabledDate,
}) => {
  const handleDateChange = (key, date) => {
    if (!date) {
      onChange(key, null)
      return
    }
    const prevValue = key === 'startDate' ? startDate : endDate
    let newValue = date
    if (prevValue) {
      newValue = date.hour(prevValue.hour()).minute(prevValue.minute())
    }
    onChange(key, newValue)
  }

  const handleTimeChange = (key, time) => {
    if (!time) return
    const prevValue = key === 'startDate' ? startDate : endDate
    const baseDate = prevValue || dayjs()
    const newValue = baseDate.hour(time.hour()).minute(time.minute())
    onChange(key, newValue)
  }

  const isTask = type === 'TASK'

  // --- [추가] 날짜 제한 로직 ---
  const disabledStartDate = (current) => {
    // 1. 부모가 설정한 범위 제한 (Epic/Story 기간 등)
    if (disabledDate && disabledDate(current)) return true
    // 2. 종료 날짜가 있으면 종료 날짜 이후는 선택 불가
    return endDate ? current.isAfter(endDate, 'day') : false
  }

  const disabledEndDate = (current) => {
    if (disabledDate && disabledDate(current)) return true
    // 3. 시작 날짜가 있으면 시작 날짜 이전은 선택 불가
    return startDate ? current.isBefore(startDate, 'day') : false
  }

  // 공통 스타일: 높이 44px
  const commonStyle = {
    height: '44px',
    borderRadius: '8px',
  }
  // --- [추가] 시간 제한 로직 (같은 날일 때만 작동) ---
  const getDisabledTime = (isStart) => {
    if (!startDate || !endDate || !startDate.isSame(endDate, 'day')) return {}

    return {
      disabledHours: () => {
        const hours = []
        if (isStart) {
          // 시작 시간은 종료 시간의 '시'보다 클 수 없음
          // for (let i = endDate.hour() + 1; i < 24; i++) hours.push(i)
        } else {
          // 종료 시간은 시작 시간의 '시'보다 작을 수 없음
          for (let i = 0; i < startDate.hour(); i++) hours.push(i)
        }
        return hours
      },
      disabledMinutes: (selectedHour) => {
        const minutes = []
        if (isStart && selectedHour === endDate.hour()) {
          // '시'가 같다면 시작 '분'은 종료 '분'보다 클 수 없음
          for (let i = endDate.minute() + 1; i < 60; i++) minutes.push(i)
        } else if (!isStart && selectedHour === startDate.hour()) {
          // '시'가 같다면 종료 '분'은 시작 '분'보다 작을 수 없음
          for (let i = 0; i < startDate.minute(); i++) minutes.push(i)
        }
        return minutes
      },
    }
  }

  return (
    <div
      className="date-picker-group"
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {/* 시작 일시 영역 */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          width: isTask ? 'auto' : '250px',
          flex: isTask ? 1 : 'none',
          flexShrink: 0,
          minWidth: isTask ? 0 : '250px',
        }}
      >
        <DatePicker
          placeholder="날짜 선택"
          value={startDate}
          format="YY년 M월 D일"
          suffixIcon={
            <CalendarFilled style={{ fontSize: '18px', color: '#585F65' }} />
          }
          disabledDate={disabledStartDate}
          onChange={(date) => handleDateChange('startDate', date)}
          style={{ ...commonStyle, width: '100%' }}
        />
        {isTask && (
          <TimePicker
            placeholder="시작 시간"
            value={startDate}
            format="HH:mm"
            minuteStep={10}
            hideDisabledOptions
            showNow={false}
            disabledTime={() => getDisabledTime(true)}
            onChange={(time) => handleTimeChange('startDate', time)}
            style={{ ...commonStyle, flex: 1 }}
            needConfirm={false}
          />
        )}
      </div>

      <span
        className="date-separator"
        style={{ color: '#A4ADB4', fontWeight: 'bold' }}
      >
        ~
      </span>

      {/* 종료 일시 영역 */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          width: isTask ? 'auto' : '250px',
          flex: isTask ? 1 : 'none',
          flexShrink: 0,
          minWidth: isTask ? 0 : '250px',
        }}
      >
        <DatePicker
          placeholder="날짜 선택"
          value={endDate}
          format="YY년 M월 D일"
          suffixIcon={
            <CalendarFilled style={{ fontSize: '18px', color: '#585F65' }} />
          }
          disabledDate={disabledEndDate}
          onChange={(date) => handleDateChange('endDate', date)}
          style={{ ...commonStyle, width: '100%' }}
        />
        {isTask && (
          <TimePicker
            placeholder="시간"
            value={endDate}
            format="HH:mm"
            minuteStep={10}
            hideDisabledOptions
            showNow={false}
            disabledTime={() => getDisabledTime(false)}
            onChange={(time) => handleTimeChange('endDate', time)}
            style={{ ...commonStyle, flex: 1 }}
            needConfirm={false}
          />
        )}
      </div>
    </div>
  )
}

const TaskCompletionTimePicker = ({
  startDate,
  reviewDate,
  // doneDate,
  startTime,
  reviewTime,
  // doneTime,
  onDateChange,
  onTimeChange,
  disabledDate,
}) => {
  // --- [추가] 시간 비활성화 로직 ---
  const getDisabledTime = (type) => {
    const isSameDay = (d1, d2) => d1 && d2 && d1.isSame(d2, 'day')

    // return {
    //   disabledHours: () => {
    //     let hours = []
    //     if (type === 'start' && isSameDay(startDate, reviewDate)) {
    //       // 시작은 리뷰보다 무조건 빨라야 함 (리뷰 시간 포함 이후 시간 비활성화)
    //       for (let i = reviewDate.hour(); i < 24; i++) hours.push(i)
    //     } else if (type === 'review') {
    //       if (isSameDay(reviewDate, startDate)) {
    //         // 리뷰는 시작보다 무조건 늦어야 함 (시작 시간 포함 이전 시간 비활성화)
    //         for (let i = 0; i <= startDate.hour(); i++) hours.push(i)
    //       }
    //       if (isSameDay(reviewDate, doneDate)) {
    //         // 리뷰는 완료와 같아도 됨 (완료 이후 시간만 비활성화)
    //         for (let i = doneDate.hour() + 1; i < 24; i++) hours.push(i)
    //       }
    //     } else if (type === 'done' && isSameDay(doneDate, reviewDate)) {
    //       // 완료는 리뷰와 같아도 됨 (리뷰 이전 시간만 비활성화)
    //       for (let i = 0; i < reviewDate.hour(); i++) hours.push(i)
    //     }
    //     return hours
    //   },
    //   disabledMinutes: (selectedHour) => {
    //     let minutes = []
    //     if (
    //       type === 'start' &&
    //       isSameDay(startDate, reviewDate) &&
    //       selectedHour === reviewDate.hour()
    //     ) {
    //       // 시작 분은 리뷰 분과 같거나 클 수 없음
    //       for (let i = reviewDate.minute(); i < 60; i++) minutes.push(i)
    //     } else if (type === 'review') {
    //       if (
    //         isSameDay(reviewDate, startDate) &&
    //         selectedHour === startDate.hour()
    //       ) {
    //         // 리뷰 분은 시작 분보다 무조건 커야 함 (시작 분 포함 이전 비활성화)
    //         for (let i = 0; i <= startDate.minute(); i++) minutes.push(i)
    //       }
    //       if (
    //         isSameDay(reviewDate, doneDate) &&
    //         selectedHour === doneDate.hour()
    //       ) {
    //         // 리뷰 분은 완료 분과 같아도 됨 (완료 분 이후만 비활성화)
    //         for (let i = doneDate.minute() + 1; i < 60; i++) minutes.push(i)
    //       }
    //     } else if (
    //       type === 'done' &&
    //       isSameDay(doneDate, reviewDate) &&
    //       selectedHour === reviewDate.hour()
    //     ) {
    //       // 완료 분은 리뷰 분과 같아도 됨 (리뷰 분 이전만 비활성화)
    //       for (let i = 0; i < reviewDate.minute(); i++) minutes.push(i)
    //     }
    //     return minutes
    //   },
    // }
    return {
      disabledHours: () => {
        let hours = []
        if (type === 'start' && isSameDay(startDate, reviewDate)) {
          // 시작 시간은 리뷰 시간의 '시'보다 클 수 없음
          for (let i = reviewDate.hour() + 1; i < 24; i++) hours.push(i)
        } else if (type === 'review' && isSameDay(reviewDate, startDate)) {
          // 리뷰 시간은 시작 시간의 '시'보다 작을 수 없음
          for (let i = 0; i < startDate.hour(); i++) hours.push(i)
        }
        return hours
      },
      disabledMinutes: (selectedHour) => {
        let minutes = []
        if (isSameDay(startDate, reviewDate)) {
          if (type === 'start' && selectedHour === reviewDate.hour()) {
            // 같은 '시'일 때, 시작 '분'은 리뷰 '분'보다 크거나 "같을 수 없음"
            // (최소 10분 차이를 두고 싶다면 reviewDate.minute() 로 수정)
            for (let i = reviewDate.minute(); i < 60; i++) minutes.push(i)
          } else if (type === 'review' && selectedHour === startDate.hour()) {
            // 같은 '시'일 때, 리뷰 '분'은 시작 '분'보다 작거나 "같을 수 없음"
            for (let i = 0; i <= startDate.minute(); i++) minutes.push(i)
          }
        }
        return minutes
      },
    }
  }

  // --- [추가] 날짜 비활성화 로직 (시작 <= 리뷰 <= 완료) ---
  // const getDisabledDateByOrder = (type, current) => {
  //   if (!current) return false
  //   // 부모(Epic/Story) 기간 제한 먼저 적용
  //   if (disabledDate && disabledDate(current)) return true

  //   if (type === 'start') {
  //     return reviewDate ? current.isAfter(reviewDate, 'day') : false
  //   } else if (type === 'review') {
  //     const isBeforeStart = startDate
  //       ? current.isBefore(startDate, 'day')
  //       : false
  //     const isAfterDone = doneDate ? current.isAfter(doneDate, 'day') : false
  //     return isBeforeStart || isAfterDone
  //   } else if (type === 'done') {
  //     return reviewDate ? current.isBefore(reviewDate, 'day') : false
  //   }
  //   return false
  // }
  const getDisabledDateByOrder = (type, current) => {
    if (!current) return false
    if (disabledDate && disabledDate(current)) return true

    if (type === 'start') {
      return reviewDate ? current.isAfter(reviewDate, 'day') : false
    } else if (type === 'review') {
      // 리뷰는 시작일 이후이기만 하면 됨
      return startDate ? current.isBefore(startDate, 'day') : false
    }
    return false
  }

  const TimeInputRow = ({ type, label, dateValue, timeValue }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 0',
      }}
    >
      <DatePicker
        placeholder="날짜 선택"
        value={dateValue}
        format="YY년 M월 D일"
        disabledDate={(current) => getDisabledDateByOrder(type, current)}
        onChange={(date) => onDateChange(type, date)}
        suffixIcon={
          <CalendarFilled style={{ fontSize: '16px', color: '#585F65' }} />
        }
        style={{
          height: '40px',
          borderRadius: '8px',
          flex: 1,
          maxWidth: '200px',
          marginLeft: '12px',
        }}
      />
      <TimePicker
        value={timeValue}
        format="HH:mm"
        placeholder="HH:mm"
        minuteStep={10}
        showNow={false}
        suffixIcon={null}
        disabledTime={() => getDisabledTime(type)}
        hideDisabledOptions
        needConfirm={false}
        onChange={(time) => onTimeChange(type, time)}
        style={{
          maxWidth: '120px',
          height: '40px',
          borderRadius: '8px',
          border: '1px solid #E2EAEF',
          marginLeft: '12px',
        }}
      />
    </div>
  )

  return (
    <div
      style={{
        display: 'flex',
        border: '1px solid #E2EAEF',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        width: '100%',
      }}
    >
      <div
        style={{
          backgroundColor: '#F6F7FA',
          padding: '16px 24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '30px',
          borderRight: '1px solid #E2EAEF',
          minWidth: '180px',
        }}
      >
        <Text
          strong
          style={{ fontSize: '16px', color: '#191A1C', marginBottom: '8px' }}
        >
          시작 시간
        </Text>
        <Text
          strong
          style={{
            fontSize: '16px',
            color: '#191A1C',
            marginBottom: '4px',
            marginTop: '4px',
          }}
        >
          리뷰 시간 (In Review)
        </Text>
        {/* <Text
          strong
          style={{ fontSize: '16px', color: '#191A1C', marginTop: '8px' }}
        >
          완료 시간 (Done)
        </Text> */}
      </div>
      <div
        style={{
          padding: '8px 12px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <TimeInputRow
          type="start"
          dateValue={startDate}
          timeValue={startTime}
        />
        <div
          style={{
            height: '1px',
            backgroundColor: '#F0F2F5',
            margin: '0 12px',
          }}
        />
        <TimeInputRow
          type="review"
          dateValue={reviewDate}
          timeValue={reviewTime}
        />
        {/* <div
          style={{
            height: '1px',
            backgroundColor: '#F0F2F5',
            margin: '0 12px',
          }}
        />
        <TimeInputRow type="done" dateValue={doneDate} timeValue={doneTime} /> */}
      </div>
    </div>
  )
}

const WorkCreatePopup = ({
  open,
  onClose,
  type,
  id = null,
  parentStoryWorkItemId,
  selectedTeam,
  parentStartDateTime,
  parentEndDateTime,
  onSuccessTaskCreate,
  fetchTodayData = () => {},
  zIndex,
}) => {
  const [user] = useRecoilState(userState)
  const isEdit = !!id

  // 메인 데이터 상태
  const [formData, setFormData] = useState({
    name: '',
    // purpose: '',
    startDate: null,
    endDate: null,
    taskReviewEndDate: null,
    priority: 'FIRST',
    progressStatus: 'TODO',
    hasProblem: false,
    epicList: [],
    epicSelect: '',
    taskList: [],
    assignedUsers: [], // 다중 선택을 위한 배열
    assignee: [],
    checkList: [], // { id, title, isChecked }
    memo: '',
    parentStartDateTime: parentStartDateTime,
    parentEndDateTime: parentEndDateTime,
    enableStates: ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'ON_HOLD'],
    parentId: null,
    parentName: null,
    parentStatus: null,
    category: '개선',
    emojiText: '✨', // TASK 전용: 이모지 문자열
  })

  // 체크리스트 변경 추적 (Diff Logic)
  const [originalCheckList, setOriginalCheckList] = useState([])
  const [deletedCheckListIds, setDeletedCheckListIds] = useState([])
  const [tempSubTask, setTempSubTask] = useState('')

  // UI 제어 상태
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState(null)
  const [selectedStudio, setSelectedStudio] = useState(null)
  const [isIssued, setIsIssued] = useState(false)
  // 컴포넌트 내부 상단에 추가
  const [isInputActive, setIsInputActive] = useState(true)
  const [editingId, setEditingId] = useState(null)

  // 입력 완료 처리
  const handleInputSubmit = () => {
    if (tempSubTask.trim()) {
      addSubTask() // 기존 항목 추가 로직
      // setIsInputActive(false) // 입력창 닫고 추가 버튼 활성화
    }
  }

  // 체크 상태 변경 핸들러
  const toggleCheckItem = (id) => {
    setFormData((prev) => ({
      ...prev,
      checkList: prev.checkList.map((item) =>
        item.id === id ? { ...item, isChecked: !item.isChecked } : item,
      ),
    }))
  }

  // '하위 업무 추가 +' 버튼 클릭 시 실행
  const handleAddNewClick = () => {
    setIsInputActive(true) // 다시 입력 칸을 보여줌
  }

  useEffect(() => {
    if (type === 'TASK') {
      setFormData((prev) => ({
        ...prev,
        parentId: parentStoryWorkItemId,
      }))
    }
  }, [type, parentStoryWorkItemId, parentStartDateTime])

  useEffect(() => {
    if (type === 'STORY') {
      setFormData((prev) => ({
        ...prev,
        parentId: formData.epicSelect,
      }))
    }
  }, [formData.epicSelect])

  // epicList가 로드되면 항상 자동 선택
  useEffect(() => {
    if (
      type === 'STORY' &&
      !isEdit &&
      formData.epicList &&
      formData.epicList.length > 0
    ) {
      setFormData((prev) => {
        const currentEpicExists = prev.epicList.some(
          (e) => e.id === prev.epicSelect,
        )
        if (!prev.epicSelect || !currentEpicExists) {
          const maxIdItem = prev.epicList.reduce((max, item) =>
            item.id > max.id ? item : max,
          )
          return { ...prev, epicSelect: maxIdItem.id }
        }
        return prev
      })
    }
  }, [formData.epicList, type, isEdit])

  useEffect(() => {
    if (type === 'TASK' || type === 'STORY') {
      // console.log('formData.parentId', formData.parentId)
      if (formData.parentId) {
        getProgressState(formData.parentId).then((res) => {
          // console.log('getProgressState res:', res)
          setFormData((prev) => ({
            ...prev,
            parentName: res.data?.name,
            parentStatus: res.data?.progressState,
            progressStatus: res.data?.progressState,
            enableStates: res.data?.childSelectableProgressStates || [
              'TODO',
              'IN_PROGRESS',
              'IN_REVIEW',
              'DONE',
              'ON_HOLD',
            ],
          }))
        })
      }
    }
  }, [formData.parentId, type])

  // WorkCreatePopup 내부의 핸들러 수정
  const handleTaskTimeChange = (type, time) => {
    if (!time) return

    const fieldMap = {
      start: 'startDate',
      review: 'taskReviewEndDate',
      // done: 'endDate',
    }
    const field = fieldMap[type]

    setFormData((prev) => {
      // 1. 기존에 해당 필드에 값이 있으면 그 날짜를 유지, 없으면 오늘 날짜 기준
      const baseDate = prev[field] || dayjs()
      const updatedTime = baseDate
        .hour(time.hour())
        .minute(time.minute())
        .second(0)

      const nextState = {
        ...prev,
        // 2. 선택된 시간의 시(hour)와 분(minute)만 가져와서 기존 날짜 객체에 덮어씌움
        [field]: updatedTime, // 초는 0으로 초기화
      }

      if (type === 'review') {
        nextState.endDate = updatedTime
      }

      return nextState
    })
  }

  // 날짜 변경 시 모든 관련 필드의 날짜를 동기화
  const handleTaskDateChange = (type, date) => {
    if (!date) return

    const fieldMap = {
      start: 'startDate',
      review: 'taskReviewEndDate',
      // done: 'endDate',
    }
    const field = fieldMap[type]

    setFormData((prev) => {
      const updatedDate = date
        .hour(prev[field]?.hour() || 9)
        .minute(prev[field]?.minute() || 0)

      const nextState = {
        ...prev,
        [field]: updatedDate,
      }

      // [핵심] 리뷰 날짜를 바꿀 때 완료 날짜(endDate)도 똑같이 맞춤
      if (type === 'review') {
        nextState.endDate = updatedDate
      }

      return nextState
    })
  }

  const handleTitleChange = (id, newTitle) => {
    setFormData((prev) => ({
      ...prev,
      checkList: prev.checkList.map((item) =>
        item.id === id ? { ...item, title: newTitle } : item,
      ),
    }))
  }
  const TaskCard = (
    { task, color, onClick }, // [수정] onClick 추가
  ) => (
    <div
      className="story-task-card"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '16px',
          height: '44px',
          flexShrink: 0,
          borderRadius: '0 0 12px 0',
          background: color,
          zIndex: 1,
        }}
      />

      <div className="card-content" style={{ paddingLeft: '24px' }}>
        <div className="card-top">
          <span className="task-label">Task</span>
          <span className="divider">|</span>
          <span className="status-text">
            {mapBackendStateToFrontend(task.progressState)}
          </span>
          <RightOutlined className="arrow-icon" style={{ fontSize: '12px' }} />
        </div>
        <div className="task-title">{task.title}</div>
        <div className="task-date">
          {dayjs(task.startDateTime).format('YY년 MM월 DD일')} -{' '}
          {dayjs(task.endDateTime).format('YY년 MM월 DD일')}
        </div>
        <div className="task-assignee">
          담당자{' '}
          <span className="name">{task.owners?.map((u) => u).join(', ')}</span>
        </div>
        {task?.hasProblem && (
          <Tag
            icon={
              <FireFilled
                style={{
                  color:
                    task?.problemState === 'RESOLVED_OVERDUE_REVIEW_TIME'
                      ? '#6D747A'
                      : '#F25A67',
                }}
              />
            }
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '12px',
              borderRadius: '6px',
              fontSize: '10px',
              margin: 0,
              background: '#fff',
              color:
                task?.problemState === 'RESOLVED_OVERDUE_REVIEW_TIME'
                  ? '#6D747A'
                  : '#F25A67',
              border:
                task?.problemState === 'RESOLVED_OVERDUE_REVIEW_TIME'
                  ? '1px solid #6D747A'
                  : '1px solid #F25A67',
              fontWeight: '700',
              padding: '0 6px',
            }}
          ></Tag>
        )}
      </div>
    </div>
  )

  // 진행 상태별 우선순위 (낮을수록 우선)
  const getProgressStateOrder = (progressState) => {
    const orderMap = {
      IN_PROGRESS: 1,
      IN_REVIEW: 2,
      TODO: 3,
      ON_HOLD: 4,
      DONE: 5,
    }
    return orderMap[progressState] || 999
  }

  const StoryTaskListSection = ({ tasks, onAddTask, onEditTask }) => {
    // tasks를 owners별로 그룹화
    const tasksByOwner = {}
    tasks?.forEach((task) => {
      task.owners?.forEach((owner) => {
        if (!tasksByOwner[owner]) {
          tasksByOwner[owner] = []
        }
        tasksByOwner[owner].push(task)
      })
    })

    // owners를 한글 가나다순으로 정렬
    const sortedOwners = Object.keys(tasksByOwner).sort((a, b) =>
      a.localeCompare(b, 'ko'),
    )

    // 각 owner 그룹 내에서 tasks를 진행 상태별로 정렬
    sortedOwners.forEach((owner) => {
      tasksByOwner[owner].sort((a, b) => {
        const orderA = getProgressStateOrder(a.progressState)
        const orderB = getProgressStateOrder(b.progressState)
        return orderA - orderB
      })
    })

    return (
      <div className="story-task-section">
        <div className="section-header">
          <span className="section-title">Initiative 내 Task 업무 목록</span>
          <button className="btn-add-task" onClick={onAddTask}>
            새로운 Task 생성 +
          </button>
        </div>
        {sortedOwners.map((owner) => (
          <div key={owner} className="owner-task-group">
            <div className="owner-name">{owner}</div>
            <div className="task-card-list">
              {tasksByOwner[owner].map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  color={mapProgressStateAndPriorityToFrontendColor(
                    task.progressState,
                    task.priority,
                  )}
                  onClick={() => onEditTask(task)}
                />
              ))}
            </div>
            <div className="owner-name-divider" />
          </div>
        ))}
      </div>
    )
  }

  /** 데이터 초기 로드 */
  useEffect(() => {
    if (open) {
      setDeletedCheckListIds([])
      if (isEdit) {
        fetchWorkDetail()
      } else {
        setFormData((prev) => ({
          ...prev,
          name: '',
          // purpose: '',
          startDate:
            type === 'TASK' && parentStartDateTime
              ? dayjs(parentStartDateTime).hour(9).minute(30)
              : null,
          endDate: null,
          priority: 'FIRST',
          progressStatus: 'TODO',
          hasProblem: false,
          epicSelect: '',
          assignedUsers: [],
          taskList: [],
          checkList: [],
          memo: '',
          enableStates: ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'ON_HOLD'],
          emojiText: type === 'TASK' ? '✨' : prev.emojiText,
        }))
        setOriginalCheckList([])
      }
    }
  }, [open, id, isEdit])

  const fetchWorkDetail = async () => {
    try {
      const res = await getWorkDetail({
        salusUserId: user.id,
        workType: type,
        workId: id,
      })

      if (res.data) {
        // console.log('res.data::', res.data)
        const { commonDetails, epicDetails, storyDetails, taskDetails } =
          res.data

        // console.log('getWorkDetail', res.data)

        // DTO의 필드명 'checkListDtos'를 사용합니다.
        const fetchedCheckList = taskDetails?.checkListDtos || []

        setFormData((prev) => ({
          ...prev,
          name: commonDetails.workTitle,
          // purpose: epicDetails?.workGoal || '',
          startDate: dayjs(commonDetails.startDateTime),
          endDate: dayjs(commonDetails.endDateTime),
          priority: commonDetails.priority,
          progressStatus: commonDetails.progressState,
          hasProblem: commonDetails.hasProblem,
          epicSelect: storyDetails?.parentEpicId || '',
          taskList:
            storyDetails?.taskList?.map((t) => ({
              ...t.work,
              owners: t.owners || [],
            })) || [],
          assignedUsers: taskDetails?.nowOwnUser
            ? [taskDetails.nowOwnUser]
            : taskDetails?.nowOwnUsers || [],
          assignee: taskDetails?.ownableUsers || [],
          // 화면에 보여줄 체크리스트 상태 업데이트
          checkList: fetchedCheckList.map((item) => ({
            id: item.id,
            title: item.title,
            isChecked: item.isChecked,
          })),
          memo: taskDetails?.memo || '',
          parentStartDateTime:
            taskDetails?.parentStartDateTime || parentStartDateTime,
          parentEndDateTime:
            taskDetails?.parentEndDateTime || parentEndDateTime,
          enableStates: storyDetails?.enableStates
            ? storyDetails?.enableStates
            : taskDetails?.enableStates
              ? taskDetails?.enableStates
              : ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'ON_HOLD'],
          category: CATEGORY_FRONTEND_MAP[storyDetails?.category] || '개선',
          taskReviewEndDate: dayjs(taskDetails?.taskReviewEndDateTime),
          emojiText:
            taskDetails?.emojiText !== undefined &&
            taskDetails?.emojiText !== null &&
            taskDetails.emojiText !== ''
              ? taskDetails.emojiText
              : (prev.emojiText ?? '✨'),
        }))

        // 변경 사항 비교를 위한 원본 저장 (동기화 로직용)
        setOriginalCheckList(JSON.parse(JSON.stringify(fetchedCheckList)))
      }
    } catch (error) {
      console.error('상세 조회 실패:', error)
    }
  }

  // 담당자 선택/해제 토글 함수 추가
  const toggleAssignee = (user) => {
    setFormData((prev) => {
      const isExist = prev.assignedUsers.some((u) => u.id === user.id)
      console.log('isExist', isExist)
      let newUsers
      if (isExist) {
        // 이미 있으면 제거
        newUsers = prev.assignedUsers.filter((u) => u.id !== user.id)
      } else {
        // 없으면 추가
        newUsers = [...prev.assignedUsers, user]
        console.log('newUsers', newUsers)
      }
      return { ...prev, assignedUsers: newUsers }
    })
  }

  /** 공통 핸들러 */
  const updateField = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }))

  const validateForm = () => {
    if (!formData.name?.trim()) {
      message.error(`${type} 명을 입력해주세요.`)
      return false
    }
    if (!formData.startDate || !formData.endDate) {
      message.error('일정을 선택해주세요.')
      return false
    }
    if (!formData.priority) {
      message.error('우선 순위를 선택해주세요.')
      return false
    }
    // if (type === 'EPIC' && !formData.purpose?.trim()) {
    //   message.error('업무 목적을 입력해주세요.')
    //   return false
    // }
    if (type === 'STORY' && !formData.epicSelect) {
      message.error('Epic을 선택해주세요.')
      return false
    }
    // Task 전용 유효성 검사
    if (type === 'TASK') {
      if (!formData.startDate || !formData.taskReviewEndDate) {
        // endDate는 내부동기화되므로 review만 체크해도 무방
        message.error('모든 일정을 선택해주세요.')
        return false
      }

      // 시작 < 리뷰
      if (!formData.startDate.isBefore(formData.taskReviewEndDate)) {
        message.error('리뷰 시간은 시작 시간보다 이후여야 합니다.')
        return false
      }

      // 리뷰 <= 완료 (내부적으로 동기화되므로 항상 true이겠지만 방어코드로 유지)
      if (formData.taskReviewEndDate.isAfter(formData.endDate)) {
        message.error('완료 시간 오류가 발생했습니다.')
        return false
      }
    } else {
      // Epic/Story용 기존 검사
      if (!formData.startDate || !formData.endDate) {
        message.error('일정을 선택해주세요.')
        return false
      }
    }
    if (
      type === 'TASK' &&
      (!formData.assignedUsers || formData.assignedUsers.length === 0)
    ) {
      message.error('담당자를 선택해주세요.')
      return false
    }
    return true
  }

  const getDisabledDate = (current) => {
    if (!current) return false
    let minDate, maxDate
    if (type === 'STORY') {
      const selectedEpic = formData.epicList.find(
        (e) => e.id === formData.epicSelect,
      )
      if (selectedEpic) {
        minDate = dayjs(selectedEpic.startDateTime)
        maxDate = dayjs(selectedEpic.endDateTime)
      }
    } else if (
      type === 'TASK' &&
      formData.parentStartDateTime &&
      formData.parentEndDateTime
    ) {
      minDate = dayjs(formData.parentStartDateTime)
      maxDate = dayjs(formData.parentEndDateTime)
    }
    if (minDate && maxDate)
      return (
        current.isBefore(minDate.startOf('day')) ||
        current.isAfter(maxDate.endOf('day'))
      )
    return false
  }

  /** 체크리스트 조작 */
  const addSubTask = () => {
    if (!tempSubTask.trim()) {
      message.warning('내용을 입력해주세요.')
      return
    }
    const newItem = {
      id: `new-${Date.now()}`,
      title: tempSubTask,
      isChecked: false,
    }
    setFormData((prev) => ({
      ...prev,
      checkList: [...prev.checkList, newItem],
    }))
    setTempSubTask('')
  }

  const removeSubTask = (targetId) => {
    // 서버에 이미 저장된 데이터인 경우 삭제 목록에 추가
    if (
      typeof targetId === 'number' ||
      !targetId.toString().startsWith('new-')
    ) {
      setDeletedCheckListIds((prev) => [...prev, targetId])
    }
    setFormData((prev) => ({
      ...prev,
      checkList: prev.checkList.filter((item) => item.id !== targetId),
    }))
  }

  /** 2. 체크리스트 동기화 로직 (ID 타입 대응) */
  const syncCheckList = async (taskId) => {
    // 1. 삭제 처리는 순서와 상관없으므로 병렬 처리해도 무방합니다.
    const deletePromises = deletedCheckListIds
      .filter((cid) => typeof cid === 'number')
      .map((cid) => deleteTaskCheckList(cid))
    await Promise.all(deletePromises)

    // 2. 생성 및 수정은 리스트의 순서대로 '순차적'으로 실행합니다.
    // for...of 루프 내에서 await를 사용하면 이전 항목 처리가 끝난 후 다음 항목으로 넘어갑니다.
    for (const item of formData.checkList) {
      const isNew = item.id.toString().startsWith('new-')

      if (isNew) {
        // 순차 생성: DB에 순서대로 INSERT 되도록 보장
        await createTaskCheckList({
          taskWorkId: taskId,
          title: item.title,
        })
      } else {
        const original = originalCheckList.find((o) => o.id === item.id)
        if (original) {
          if (original.title !== item.title) {
            await updateTaskCheckListTitle(item.id, item.title)
          }
          if (original.isChecked !== item.isChecked) {
            await switchTaskCheckList(item.id)
          }
        }
      }
    }
  }
  const getWorkCardTitle = (createType) => {
    switch (createType) {
      case 'STORY':
        return 'Initiative'
      case 'EPIC':
        return 'Objective'
      case 'TASK':
        return 'Task'
      default:
        return ''
    }
  }
  /** 최종 제출 */
  const handleSubmit = async () => {
    if (!validateForm()) return

    const commonPayload = {
      salusUserId: user.id,
      title: formData.name,
      startDateTime: formData.startDate.format('YYYY-MM-DD HH:mm:ss'),
      endDateTime: formData.endDate.format('YYYY-MM-DD HH:mm:ss'),
      priority: formData.priority,
      progressState: formData.progressStatus,
      hasProblem: formData.hasProblem,
    }
    const notTaskCommonPayload = {
      salusUserId: user.id,
      title: formData.name,
      startDateTime: formData.startDate.format('YYYY-MM-DD HH:mm:ss'),
      endDateTime: formData.endDate.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      priority: formData.priority,
      progressState: formData.progressStatus,
      hasProblem: formData.hasProblem,
    }

    const taskOwnerIds = formData.assignedUsers
      .map((u) => u.id)
      .filter((id) => id !== null && id !== undefined)
    // console.log('taskOwnerIds', taskOwnerIds)

    try {
      let currentTaskId = id
      if (isEdit) {
        if (type === 'EPIC')
          await updateEpic({
            epicWorkItemId: id,
            commonWorkItem: notTaskCommonPayload,
            // workGoal: formData.purpose,
          })
        else if (type === 'STORY')
          await updateStory({
            storyWorkItemId: id,
            parentEpicWorkItemId: formData.epicSelect,
            category: CATEGORY_BACKEND_MAP[formData.category],
            commonWorkItem: notTaskCommonPayload,
          })
        else if (type === 'TASK') {
          await updateTask({
            taskWorkItemId: id,
            taskOwnerIds: taskOwnerIds,
            commonWorkItem: commonPayload,
            memo: formData.memo,
            reviewEndDateTime: formData.taskReviewEndDate?.format(
              'YYYY-MM-DD HH:mm:ss',
            ),
            emojiText: formData.emojiText ?? '',
          })
        }
      } else {
        if (type === 'EPIC')
          await createEpic({
            commonWorkItem: notTaskCommonPayload,
            // workGoal: formData.purpose,
            studioName: selectedStudio,
          })
        else if (type === 'STORY')
          await createStory({
            commonWorkItem: notTaskCommonPayload,
            parentEpicWorkItemId: formData.epicSelect,
            category: CATEGORY_BACKEND_MAP[formData.category],
            teamName: selectedTeam,
          })
        else if (type === 'TASK') {
          const res = await createTask({
            commonWorkItem: commonPayload,
            taskOwnerIds: taskOwnerIds,
            parentStoryWorkItemId: parentStoryWorkItemId,
            memo: formData.memo,
            reviewEndDateTime: formData.taskReviewEndDate?.format(
              'YYYY-MM-DD HH:mm:ss',
            ),
            emojiText: formData.emojiText ?? '',
          })
          currentTaskId = res.data
        }
      }

      // TASK 타입인 경우 체크리스트 동기화 수행
      if (type === 'TASK') {
        await syncCheckList(currentTaskId)
      }

      message.success('성공적으로 저장되었습니다.')
      if (onSuccessTaskCreate) onSuccessTaskCreate()
      onClose()
    } catch (error) {
      console.error('저장 실패:', error)
      message.error('저장 중 오류가 발생했습니다.')
    }
  }

  /** 부모 리스트 및 팀 정보 로드 */
  useEffect(() => {
    if (open && selectedTeam) {
      // if (type === 'STORY') {
      //   getParentWorkItems({ teamName: selectedTeam }).then(
      //     (res) => res.data && updateField('epicList', res.data),
      //   )
      // }
      if (type === 'STORY') {
        getParentWorkItems({ teamName: selectedTeam }).then((res) => {
          if (res.data && res.data.length > 0) {
            // epicList 업데이트 후 현재 epicSelect 상태를 확인하여 자동 선택
            setFormData((prev) => {
              const currentEpicExists = res.data.some(
                (e) => e.id === prev.epicSelect,
              )
              const shouldAutoSelect = !prev.epicSelect || !currentEpicExists
              const maxIdItem = res.data.reduce((max, item) =>
                item.id > max.id ? item : max,
              )
              return {
                ...prev,
                epicList: res.data,
                epicSelect: shouldAutoSelect ? maxIdItem.id : prev.epicSelect,
              }
            })
          }
        })
      }

      if (type === 'TASK') {
        getTaskOwnables({ teamName: selectedTeam }).then((res) => {
          if (res.data) {
            updateField('assignee', res.data)
          }
        })
      }
      getGroupByTeam({ teamName: selectedTeam }).then(
        (res) => res.data && setSelectedStudio(res.data.studio),
      )
    }
  }, [open, type, selectedTeam])

  return (
    <ConfigProvider locale={locale}>
      <AopsCommonModalLayout
        title={
          isEdit
            ? `${getWorkCardTitle(type)} 업무`
            : `${getWorkCardTitle(type)} 업무 생성하기`
        }
        onClose={onClose}
        open={open}
        zIndex={zIndex}
      >
        <div className="form-container">
          <div className="form-wrapper">
            {/* 업무명 */}
            {/* 2. 공통: 명칭 입력 (TASK일 때 왼쪽에 EmojiDropDown) */}
            <FormSection
              label={`${getWorkCardTitle(type)} 명`}
              helperTitle={`${getWorkCardTitle(type)} 업무란?`}
              helperDescText={HELPER_DESC[type]}
            >
              {type === 'TASK' ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                  }}
                >
                  <EmojiDropDown
                    value={formData.emojiText}
                    defaultValue={
                      formData.emojiText !== undefined &&
                      formData.emojiText !== null &&
                      formData.emojiText !== ''
                        ? formData.emojiText
                        : '✨'
                    }
                    onChange={(data) =>
                      updateField(
                        'emojiText',
                        typeof data === 'string' ? data : (data?.emoji ?? ''),
                      )
                    }
                  />
                  <Input
                    placeholder="이니셔티브를 달성하기 위해 실제로 수행할 작업을 작성해주세요"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                </div>
              ) : (
                <Input
                  placeholder={
                    type === 'EPIC'
                      ? '이번 프로젝트를 통해 가져가야 할 방향성을 작성해주세요'
                      : type === 'STORY'
                        ? '오브젝티브를 달성하기 위해 해결해야 할 목적을 작성해주세요'
                        : '이니셔티브를 달성하기 위해 실제로 수행할 작업을 작성해주세요'
                  }
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                />
              )}
            </FormSection>

            {/* [STORY] 에픽 선택 (박스 스타일) */}
            {type === 'STORY' && (
              <FormSection label="Objective 선택">
                <div
                  style={{
                    border: '1px solid #E2EAEF',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div
                    style={{
                      background: '#F6F7FA',
                      padding: '12px 16px',
                      borderBottom: '1px solid #E2EAEF',
                      fontSize: '15px',
                      fontWeight: '700',
                      color: '#383E44',
                    }}
                  >
                    Objective 목록
                  </div>
                  <div
                    style={{
                      background: '#FFFFFF',
                      padding: '16px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                    }}
                  >
                    <ModalStatusButtonGroup
                      isRound={true}
                      options={formData.epicList?.map((e) => e.title) || []}
                      value={
                        formData.epicList?.find(
                          (e) => e.id === formData.epicSelect,
                        )?.title
                      }
                      onChange={(title) =>
                        updateField(
                          'epicSelect',
                          formData.epicList.find((e) => e.title === title)?.id,
                        )
                      }
                    />
                  </div>
                </div>
              </FormSection>
            )}

            {type === 'STORY' && (
              <FormSection label="업무 유형">
                <ModalStatusButtonGroup
                  options={['개선', '신규', '정책/기준', '사고 대응', '기타']}
                  value={formData.category}
                  onChange={(v) => updateField('category', v)}
                />
              </FormSection>
            )}

            {/* [EPIC] 목적 */}
            {/* {type === 'EPIC' && (
              <FormSection label="목적">
                <Input
                  placeholder="오브젝티브가 필요한 이유를 작성해주세요"
                  className="form-input"
                  value={formData.purpose}
                  onChange={(e) => updateField('purpose', e.target.value)}
                />
              </FormSection>
            )} */}

            {/* 공통: 일정, 우선순위, 상태 */}
            {type === 'TASK' ? (
              <FormSection label="업무 일정">
                <TaskCompletionTimePicker
                  // 각 행에 맞는 날짜 데이터 전달
                  startDate={formData.startDate}
                  reviewDate={formData.taskReviewEndDate}
                  doneDate={formData.endDate}
                  // 시/분 추출
                  startTime={formData.startDate}
                  reviewTime={formData.taskReviewEndDate}
                  doneTime={formData.endDate}
                  // 수정된 핸들러 연결
                  onDateChange={handleTaskDateChange}
                  onTimeChange={handleTaskTimeChange}
                  disabledDate={getDisabledDate}
                  needConfirm={false}
                />
              </FormSection>
            ) : (
              <FormSection label="일정">
                <DateRangePicker
                  type={type}
                  disabledDate={getDisabledDate}
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  onChange={updateField}
                />
              </FormSection>
            )}
            <FormSection label="우선 순위">
              <ModalStatusButtonGroup
                options={[
                  { label: '1순위', color: '#E86464' },
                  { label: '2순위', color: '#F4C444' },
                  { label: '3순위', color: '#64C494' },
                ]}
                value={mapPriorityToFrontend(formData.priority)}
                onChange={(v) =>
                  updateField('priority', mapPriorityToBackend(v))
                }
              />
            </FormSection>
            <FormSection label="진행 상태">
              <ModalStatusButtonGroup
                isRound={true}
                options={[
                  'To do',
                  'In progress',
                  'In review',
                  'Done',
                  'On hold',
                ]}
                value={mapBackendStateToFrontend(formData.progressStatus)}
                onChange={(v) =>
                  updateField('progressStatus', mapFrontendStateToBackend(v))
                }
                disabledOptions={[
                  'TODO',
                  'IN_PROGRESS',
                  'IN_REVIEW',
                  'DONE',
                  'ON_HOLD',
                ]
                  .filter((state) => !formData.enableStates.includes(state))
                  .map((state) => mapBackendStateToFrontend(state))}
                onClickDisabled={() => {
                  const func = (name, status) => {
                    switch (status) {
                      case 'TODO':
                        return (
                          name +
                          ' 업무가 To Do인 상태에서는 해당 상태로 이동할 수 없어요.'
                        )
                      case 'IN_PROGRESS':
                      case 'IN_REVIEW':
                        return '오류가 발생했습니다.'
                      case 'DONE':
                        return name + ' 업무가 이미 Done 상태예요.'
                      case 'ON_HOLD':
                        return name + ' 업무가 이미 On hold 상태예요.'
                      default:
                        return '업무의 하위 업무는 선택할 수 없습니다.'
                    }
                  }
                  const errorMessage = func(
                    formData.parentName,
                    formData.parentStatus,
                  )
                  message.error(errorMessage)
                }}
              />
            </FormSection>

            {/* [TASK] 담당자, 하위업무, 메모 */}
            {type === 'TASK' && (
              <>
                <FormSection label="담당자">
                  <ModalStatusButtonGroup
                    isRound={true}
                    isMultiple={true}
                    // 1. options를 [{id, label}] 형태로 변환해서 전달
                    options={
                      formData.assignee?.map((u) => ({
                        id: u.id,
                        label: u.name,
                      })) || []
                    }
                    // 2. 현재 선택된 담당자들의 'ID' 배열만 전달
                    value={formData.assignedUsers.map((u) => u.id)}
                    // 3. 클릭 시 넘어오는 것은 'ID'
                    onChange={(clickedId) => {
                      const selectedUser = formData.assignee.find(
                        (u) => u.id === clickedId,
                      )
                      console.log('clickedId', clickedId)
                      console.log('selectedUser', selectedUser)
                      if (selectedUser) {
                        toggleAssignee(selectedUser)
                      }
                    }}
                  />
                </FormSection>

                <FormSection label="하위업무">
                  <div
                    style={{
                      border: '1px solid #E2EAEF',
                      borderRadius: '12px',
                      // overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* 헤더 */}
                    <div
                      style={{
                        background: '#F6F7FA',
                        padding: '12px 16px',
                        borderBottom: '1px solid #E2EAEF',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '15px',
                          fontWeight: '700',
                          color: '#383E44',
                        }}
                      >
                        하위업무 생성하기
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#8A949E',
                          marginTop: '2px',
                        }}
                      >
                        이 테스크에서 체크해야 할 업무를 적어보세요
                      </div>
                    </div>

                    {/* 그리드 영역 */}
                    <div
                      style={{
                        background: '#FFFFFF',
                        padding: '16px',
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          columnGap: '40px', // [수정] 32px에서 40px로 늘려서 X 버튼 공간 확보
                          rowGap: '12px',
                          width: '100%',
                          paddingRight: '15px', // [수정] 오른쪽 끝 버튼이 잘리지 않도록 여백 추가
                        }}
                      >
                        {/* 1. 저장된 항목 리스트 */}
                        {formData?.checkList?.map((item) => (
                          <div
                            key={item.id}
                            style={{
                              display: 'flex', // 가로 배열
                              alignItems: 'center', // 세로 중앙 정렬
                              gap: '8px', // 입력 칸과 X 버튼 사이의 확실한 간격
                              width: '100%',
                              minWidth: 0,
                            }}
                          >
                            {/* 실제 내용이 담긴 입력 칸 모양의 박스 */}
                            <div
                              style={{
                                flex: 1, // 버튼을 제외한 나머지 공간을 꽉 채움 (너비 자동 조절)
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 12px',
                                background: '#FFFFFF',
                                borderRadius: '8px',
                                border: `1px solid ${item.isChecked ? '#E2EAEF' : '#383E44'}`,
                                height: '44px',
                                minWidth: 0, // 글자가 길어질 때 박스가 깨지는 것 방지
                              }}
                            >
                              <Checkbox
                                checked={item.isChecked}
                                onChange={() => toggleCheckItem(item.id)}
                                className="custom-circle-checkbox"
                                style={{ marginRight: '10px' }}
                              />
                              {editingId === item.id ? (
                                <Input
                                  value={item.title}
                                  variant="borderless" // 하단 입력칸과 동일한 스타일
                                  autoFocus
                                  onBlur={() => setEditingId(null)}
                                  onPressEnter={() => setEditingId(null)}
                                  onChange={(e) =>
                                    handleTitleChange(item.id, e.target.value)
                                  }
                                  style={{
                                    padding: 0,
                                    fontSize: 'inherit', // 부모의 14px 상속
                                    fontWeight: 'inherit', // 부모의 600 상속
                                    color: 'inherit', // 부모의 색상 상속
                                  }}
                                />
                              ) : (
                                <div
                                  onClick={() => setEditingId(item.id)}
                                  style={{
                                    flex: 1,
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    // [수정] 체크 시 색상을 파란색(#007AFF)으로 변경
                                    color: item.isChecked
                                      ? '#007AFF'
                                      : '#191a1c',
                                    // 취소선 설정 (색상은 위 color 값을 자동으로 따름)
                                    textDecoration: item.isChecked
                                      ? 'line-through'
                                      : 'none',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {item.title}
                                </div>
                              )}
                            </div>

                            {/* 오른쪽 삭제 버튼: 이제 테두리 밖 '독립적인 공간'에 존재합니다 */}
                            <div
                              onClick={() => removeSubTask(item.id)}
                              style={{
                                flexShrink: 0, // 버튼 크기가 줄어들지 않도록 고정
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#E2EAEF',
                                borderRadius: '50%',
                                cursor: 'pointer',
                              }}
                            >
                              <span
                                style={{
                                  color: '#8A949E',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                }}
                              >
                                ×
                              </span>
                            </div>
                          </div>
                        ))}

                        {/* 2. 현재 입력 중인 세트 (활성화 시에만 노출) */}
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              display: 'flex',
                              alignItems: 'center',
                              padding: '0 12px',
                              background: '#FFFFFF',
                              borderRadius: '8px',
                              border: '1px solid #E2EAEF',
                              height: '44px',
                            }}
                          >
                            <Input
                              placeholder="하위업무를 적어주세요"
                              value={tempSubTask}
                              variant="borderless"
                              style={{ flex: 1, padding: 0, minWidth: 0 }}
                              onChange={(e) => setTempSubTask(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  // e.nativeEvent.isComposing이 true이면 한글 조합 중이므로 실행하지 않음
                                  if (e.nativeEvent.isComposing) return

                                  handleInputSubmit()
                                }
                              }}
                            />
                          </div>
                          <button
                            onClick={handleInputSubmit}
                            disabled={!tempSubTask.trim()}
                            style={{
                              width: '52px',
                              height: '44px',
                              background: '#FFFFFF',
                              border: `1px solid ${tempSubTask.trim() ? '#007AFF' : '#D1D5DB'}`,
                              color: tempSubTask.trim() ? '#007AFF' : '#D1D5DB',
                              borderRadius: '8px',
                              cursor: tempSubTask.trim()
                                ? 'pointer'
                                : 'not-allowed',
                              fontWeight: '600',
                            }}
                          >
                            입력
                          </button>
                        </div>

                        {/* 3. 하위 업무 추가 버튼 (입력이 완료된 상태에서 활성화) */}
                        {/* <button
                          onClick={() => setIsInputActive(true)}
                          disabled={isInputActive}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#FFFFFF',
                            border: `1px solid ${!isInputActive ? '#007AFF' : '#D1D5DB'}`,
                            color: !isInputActive ? '#007AFF' : '#D1D5DB',
                            borderRadius: '8px',
                            height: '44px',
                            cursor: !isInputActive ? 'pointer' : 'default',
                            fontSize: '14px',
                            fontWeight: '600',
                            transition: 'all 0.2s',
                          }}
                        >
                          하위 업무 추가 +
                        </button> */}
                      </div>
                    </div>
                  </div>
                </FormSection>

                <FormSection label="메모">
                  <div style={{ position: 'relative' }}>
                    <Input
                      placeholder="업무 관련한 간단한 메모를 적어주세요"
                      value={formData.memo}
                      maxLength={200}
                      onChange={(e) => updateField('memo', e.target.value)}
                      style={{
                        paddingRight: '65px',
                        borderRadius: '8px',
                        height: '44px',
                      }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '12px',
                        color: '#A4ADB4',
                      }}
                    >
                      {formData.memo?.length || 0} / 200자
                    </span>
                  </div>
                </FormSection>
              </>
            )}

            {/* [STORY] 하단 Task 리스트 */}
            {/* [STORY] 하단 Task 리스트 복구 */}
            {type === 'STORY' && isEdit && (
              <div className="form-section vertical">
                <label className="form-label">현재 Task</label>
                <StoryTaskListSection
                  tasks={formData.taskList}
                  onAddTask={() => {
                    setSelectedTaskId(null) // 신규 생성이므로 ID 비움
                    setIsTaskModalOpen(true)
                  }}
                  onEditTask={(task) => {
                    setSelectedTaskId(task.id) // 클릭한 Task ID 설정
                    setIsTaskModalOpen(true)
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className="popup-footer">
          <button
            className="btn-cancel"
            onClick={async () => {
              if (isEdit) {
                try {
                  if (type === 'EPIC') {
                    await deleteEpic({
                      data: {
                        salusUserId: user.id,
                        workItemId: id,
                      },
                    })
                  } else if (type === 'STORY') {
                    await deleteStory({
                      data: {
                        salusUserId: user.id,
                        workItemId: id,
                      },
                    })
                  } else if (type === 'TASK') {
                    await deleteTask({
                      data: {
                        salusUserId: user.id,
                        workItemId: id,
                      },
                    })
                  }
                } catch (err) {
                  console.error('삭제 실패:', err)
                  message.error('삭제 중 오류가 발생했습니다.')
                  return
                }
              }
              if (fetchTodayData) fetchTodayData()
              onClose()
            }}
          >
            {isEdit ? '삭제' : '취소'}
          </button>
          <button className="btn-submit" onClick={handleSubmit}>
            {isEdit ? '수정 완료' : '등록'}
          </button>
        </div>

        {/* 중첩 모달 (Story에서 Task 생성 시) */}
        {isTaskModalOpen && (
          <WorkCreatePopup
            id={selectedTaskId}
            open={isTaskModalOpen}
            onClose={() => {
              setIsTaskModalOpen(false)
              fetchWorkDetail()
            }}
            type="TASK"
            parentStoryWorkItemId={id}
            parentStartDateTime={formData.startDate}
            parentEndDateTime={formData.endDate}
            selectedTeam={selectedTeam}
            onSuccessTaskCreate={fetchWorkDetail}
          />
        )}
      </AopsCommonModalLayout>
    </ConfigProvider>
  )
}

export default WorkCreatePopup
